import React, { useCallback, useMemo, useRef } from 'react';
import { getAvailableOrientations, getOrientationDescription } from '../../utils/orientation';
import { ORIENTATIONS } from 'engage/constants/projectCreatorConsts';
import Icon from 'engage/components/Icon/Icon';
import styles from './OrientationsDropdown.module.scss';
import cx from 'classnames';
import { CustomButton } from 'engage/components/Button/Button';
import { useTranslation } from 'react-i18next';
import useOutsideClick from 'engage/hooks/useOutsideClick';
import Tooltip from 'engage/components/Tooltip/Tooltip';

export const OrientationsDropdown = ({ videoOrientation, scenes, onChange }) => {
    const contentRef = useRef(null);

    const [showOrientationOptions, setShowOrientationOptions] = React.useState(false);

    const [hoverOption, setHoverOption] = React.useState(null);

    useOutsideClick(contentRef, () => {
        if (showOrientationOptions) {
            setShowOrientationOptions(false);
        }
    }
    );

    const { t } = useTranslation();

    const orientationOptions = useMemo(() => {
        const availableOrientations = getAvailableOrientations(scenes);
        return Object.keys(ORIENTATIONS).map(key => {
            const option = {
                id: ORIENTATIONS[key].type,
                text: <span className={styles.orientation}>{t(ORIENTATIONS[key].text)} <span className={styles.hint}>{(`(${getOrientationDescription(ORIENTATIONS[key].type)})`)}</span></span>,
                disabled: !availableOrientations.includes(ORIENTATIONS[key].type)
            }
            return option;
        }
        );
    }, [t, scenes])

    const handleSelectOption = useCallback((option) => {
        if (!option.disabled && videoOrientation.type !== option.id) {
            onChange(option.id);
        }
        setShowOrientationOptions(false);
    }, [videoOrientation, onChange]);

    const toggleDropdown = () => {
        setShowOrientationOptions(prevState => !prevState);
    }

    return <div ref={contentRef} className={styles.orientationDropdown}>
        <div className={styles.orientationContainer}>
            <span className={styles.orientation} onClick={toggleDropdown}>{t(videoOrientation?.text)}</span>
            <span className={styles.hint}>{(`(${getOrientationDescription(videoOrientation.type)})`)}</span>
            <CustomButton variant="link" className={styles.orientationBtn} onClick={toggleDropdown}>
                <Icon src="arrowDown" className={cx(styles.icon, { [styles.open]: showOrientationOptions })} />
            </CustomButton>
        </div>
        <ul className={cx(styles.options, { [styles.open]: showOrientationOptions })} >
            {orientationOptions.map((option) => (
                <li key={option.id}
                    className={cx(styles.option, { [styles.disabled]: option.disabled })}
                    onClick={() => handleSelectOption(option)}
                    onMouseOver={(e) => { e.stopPropagation(); option.disabled && setHoverOption({ id: option.id, top: e.clientY }); }}
                    onMouseLeave={() => setHoverOption(null)}
                >
                    <Icon src={videoOrientation.type === option.id ? 'storyboardCheckboxEnabled' : 'storyboardCheckboxDisabled'} />
                    <span>{option.text}</span>
                </li>
            ))}
        </ul>
        <Tooltip
            text={t('storyboard.generating.tooltip_orientation')}
            isHover={hoverOption !== null}
            classNameTooltip={styles.tooltip}
            moveable={false}
            tooltipStyle={{ minWidth: '250px', display: 'fixed', top: `${hoverOption?.top - 100}px`, left: `${contentRef?.current?.offsetWidth + 20}px`, transform: 'translateX(-50%)' }}
        />
    </div>
}